import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-file-list-2-line text-background-50 text-lg"></i>
          </div>
          <span
            className={`font-heading text-lg md:text-xl font-semibold transition-colors duration-300 ${
              scrolled || !isHome ? "text-foreground-900" : "text-background-50"
            }`}
          >
            USFansSpreadsheet
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            <i className="ri-home-4-line text-sm mr-1"></i>
            Strona Główna
          </Link>
          <Link
            to="/artykul/jak-korzystac"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            <i className="ri-shopping-bag-3-line text-sm mr-1"></i>
            Poradnik Zakupowy
          </Link>
          <a
            href="/#kategorie"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            Kategorie
          </a>
          <a
            href="/#porownanie"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            Porównanie
          </a>
          <a
            href="/#artykuly"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            Artykuły
          </a>
          <a
            href="/#faq"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${
              scrolled || !isHome ? "text-foreground-700 hover:text-primary-600" : "text-background-200 hover:text-background-50"
            }`}
          >
            FAQ
          </a>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Kup teraz
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled || !isHome ? "bg-background-100 text-foreground-800" : "bg-background-50/20 text-background-50"
            }`}
            aria-label={mobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[57px] z-40 bg-background-50 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            <div>
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-800 font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-home-4-line text-primary-600 text-base"></i>
                </div>
                Strona Główna
              </Link>
            </div>

            <div>
              <Link
                to="/artykul/jak-korzystac"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-800 font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-shopping-bag-3-line text-accent-600 text-base"></i>
                </div>
                Poradnik Zakupowy
              </Link>
            </div>

            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 mb-3">Kategorie produktów</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, i) => (
                  <a
                    key={i}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs flex-shrink-0"></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 mb-3">Nawigacja</p>
              <div className="space-y-1">
                <a
                  href="/#kategorie"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground-700 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-grid-line text-foreground-500"></i>
                  Kategorie
                </a>
                <a
                  href="/#porownanie"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground-700 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-scales-line text-foreground-500"></i>
                  Porównanie
                </a>
                <a
                  href="/#artykuly"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground-700 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-article-line text-foreground-500"></i>
                  Artykuły
                </a>
                <a
                  href="/#faq"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground-700 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-question-line text-foreground-500"></i>
                  FAQ
                </a>
              </div>
            </div>

            <div className="pt-2 pb-8">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Kup teraz na kaspreadsheet.com
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}